'use client'

import { useEffect, useRef, useState } from 'react'

import { tw } from '@/lib/utils'

// The bar shows once the sentinel (this component's position in flow) rises
// into the upper third of the viewport — while the hero is still exiting, not
// only after it's fully gone — and stays until the user is back near the top.
const SHOW_LINE = 0.35

// Once shown, the bar persists through upward scrolling (the site nav stays
// hidden on compare pages while scrolling up — see use-scroll-adjust.tsx) and
// only hands off once the user is back at the top, where the nav re-appears.
// Matches the nav's `scrollOffset` so the swap happens in one moment.
const TOP_EXIT_PX = 20

// Renders a zero-height sentinel in normal flow and shows `children` as a
// fixed top bar once the page has scrolled past it. The bar's top edge tracks
// the site nav's visual bottom, so whenever the nav is on screen (at the top,
// or with the menu open) the bar rides BELOW it instead of being covered.
export const StickyBarShell = ({ children }: { children: React.ReactNode }) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const visibleRef = useRef(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    const bar = barRef.current
    if (!sentinel || !bar) return

    // The site nav is the first <nav> in the document (rendered by the shell
    // before any page content).
    const navEl = document.querySelector('nav')

    let raf = 0
    const positionBar = () => {
      // The top handoff: the bar stays latched through upward scrolling and
      // releases only here, when the nav is about to take the top edge back.
      if (visibleRef.current && window.scrollY <= TOP_EXIT_PX) {
        visibleRef.current = false
        setVisible(false)
      }
      // While hidden, park at top 0 so -translate-y-full puts the bar fully
      // off-screen regardless of the nav offset.
      const navBottom = navEl && visibleRef.current ? Math.max(0, navEl.getBoundingClientRect().bottom) : 0
      bar.style.top = `${navBottom}px`
    }
    const schedule = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(positionBar)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // With the shrunken root, "not intersecting" also fires while the
        // sentinel is still BELOW the viewport — require it to be above the
        // show line.
        const passedShowLine = !entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight * SHOW_LINE
        if (passedShowLine) {
          // Scrolled past the hero → latch the bar on.
          visibleRef.current = true
          setVisible(true)
        } else if (window.scrollY <= TOP_EXIT_PX) {
          // Back at the top (e.g. a jump-to-top that outran the scroll
          // handler) → release.
          visibleRef.current = false
          setVisible(false)
        }
        // Otherwise the sentinel re-entered the viewport mid-page — the user
        // is scrolling back up. Keep the bar latched; positionBar releases it
        // at the top.
        schedule()
      },
      { rootMargin: `-${Math.round(SHOW_LINE * 100)}% 0px 0px 0px` }
    )
    observer.observe(sentinel)

    positionBar()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    // Catch the end of the nav's own show/hide transform transition when it
    // finishes after scrolling stops.
    navEl?.addEventListener('transitionend', schedule)

    return () => {
      observer.disconnect()
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      navEl?.removeEventListener('transitionend', schedule)
    }
  }, [])

  return (
    <>
      <div ref={sentinelRef} aria-hidden className="h-0" />
      <div
        ref={barRef}
        inert={!visible}
        className={tw(
          'fixed inset-x-0 top-0 z-40 transition-transform duration-300 ease-out',
          visible ? 'translate-y-0' : '-translate-y-full'
        )}
      >
        {children}
      </div>
    </>
  )
}
